import { ApiErrorSchema, type ErrorCode } from '@siteday/shared';
import type { z } from 'zod';

/** API base (doc 05: /api/v1). In mock mode MSW intercepts the same origin. */
export const API_URL: string = (import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1').replace(/\/$/, '');
export const IS_MOCK: boolean = import.meta.env.VITE_MOCK === '1';

export type ClientErrorCode = ErrorCode | 'NETWORK' | 'BAD_RESPONSE';

/** Every API error body is { error: { code, message, details? } } (doc 05). */
export class ApiError extends Error {
  readonly code: ClientErrorCode;
  readonly status: number;
  readonly details: unknown[] | undefined;
  constructor(code: ClientErrorCode, message: string, status: number, details?: unknown[]) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

export function errorMessage(e: unknown): string {
  if (isApiError(e)) return e.message;
  if (e instanceof Error) return e.message;
  return 'Something went wrong';
}

interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PATCH';
  body?: unknown;
  schema?: z.ZodType<T, z.ZodTypeDef, unknown>;
}

/** fetch wrapper: the `sd_support` cookie rides along (credentials: include); responses are parsed with the shared schemas. */
export async function request<T = unknown>(path: string, opts: RequestOptions<T> = {}): Promise<T> {
  const { method = 'GET', body, schema } = opts;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method,
      credentials: 'include',
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    throw new ApiError('NETWORK', 'Connection lost. Check your network and try again.', 0);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    const parsed = ApiErrorSchema.safeParse(json);
    if (parsed.success) {
      const { code, message, details } = parsed.data.error;
      throw new ApiError(code, message, res.status, details);
    }
    throw new ApiError(res.status === 401 ? 'UNAUTHENTICATED' : 'INTERNAL', `Request failed (${res.status})`, res.status);
  }
  if (!schema) return json as T;
  const out = schema.safeParse(json);
  if (!out.success) {
    if (import.meta.env.DEV) console.error('Response did not match contract', path, out.error.flatten());
    throw new ApiError('BAD_RESPONSE', `Unexpected response from ${path}`, res.status);
  }
  return out.data;
}

export const api = {
  get: <T>(path: string, schema?: z.ZodType<T, z.ZodTypeDef, unknown>) => request<T>(path, schema ? { schema } : {}),
  post: <T>(path: string, body?: unknown, schema?: z.ZodType<T, z.ZodTypeDef, unknown>) =>
    request<T>(path, { method: 'POST', ...(body !== undefined ? { body } : {}), ...(schema ? { schema } : {}) }),
  patch: <T>(path: string, body: unknown, schema?: z.ZodType<T, z.ZodTypeDef, unknown>) =>
    request<T>(path, { method: 'PATCH', body, ...(schema ? { schema } : {}) }),
};
